import React from 'react';
import { Select, Spin, Flex } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE_SIZE, MID_SEARCH_PAGE_SIZE } from 'src/utils/constants';

/**
 * Props interface for the SearchableSelect component
 */
interface SearchableSelectProps {
  /** Placeholder text for the select input */
  placeholder: string;
  /** Array of options to display in the dropdown */
  options: DefaultOptionType[];
  /** Whether the component is currently loading data */
  loading: boolean;
  /** Callback function triggered when user types in the search input */
  onSearch: (params: API.FetchParams) => void;
  /** Current selected value */
  value?: string;
  /** Callback function triggered when selection changes */
  onChange?: (value: string) => void;
  /** Additional CSS class name */
  className?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Whether to show clear button */
  allowClear?: boolean;
  /** Custom styles for the select component */
  style?: React.CSSProperties;
  /** Pagination response object to control when to show search more options */
  paginationResponse?: API.PaginationResponse;
}

const extraItemStyle: React.CSSProperties = {
  cursor: 'pointer',
  textAlign: 'center',
};

const SEARCH_MORE_VALUE = '__search_more__';

/**
 * A reusable searchable select component with loading state support.
 * This component provides a consistent interface for searchable dropdown selects
 * with loading indicators and debounced search functionality.
 *
 * @component
 * @param {SearchableSelectProps} props - The component props
 * @returns {JSX.Element} A searchable select dropdown with loading state
 */
const SearchableSelect: React.FC<SearchableSelectProps> = ({
  placeholder,
  options,
  loading,
  onSearch,
  value,
  onChange,
  className,
  disabled = false,
  allowClear = true,
  style,
  paginationResponse,
}) => {
  const { t } = useTranslation();

  // Determine if we should show the "search more" option based on pagination
  const shouldShowSearchMore = React.useMemo(() => {
    if (!onSearch) return false;

    // If no pagination response, show based on options length reaching default page size
    if (!paginationResponse) {
      return options.length >= DEFAULT_PAGE_SIZE;
    }

    // Show if there are more items available than currently displayed
    return paginationResponse.total > options.length;
  }, [onSearch, paginationResponse, options.length]);

  // Add "Search for more" option to the options array if enabled
  const enhancedOptions = React.useMemo(() => {
    if (!shouldShowSearchMore) return options;
    return [
      ...options,
      {
        label:
          paginationResponse?.size === DEFAULT_PAGE_SIZE ? (
            <div
              style={extraItemStyle}
              onClick={(e) => {
                e.stopPropagation();
                onSearch!({ search: '', limit: MID_SEARCH_PAGE_SIZE });
              }}
            >
              <b>...</b>
            </div>
          ) : (
            <div style={extraItemStyle}>
              <b>{t('Type to search more results')}</b>
            </div>
          ),
        value: SEARCH_MORE_VALUE,
        disabled: paginationResponse?.size !== DEFAULT_PAGE_SIZE,
      },
    ];
  }, [options, shouldShowSearchMore, paginationResponse?.size, onSearch, t]);

  const handleChange = (selectedValue: string) => {
    // Ignore the "Search for more" option selection
    if (selectedValue === SEARCH_MORE_VALUE) return;
    onChange?.(selectedValue);
  };

  const handleSearch = (searchValue: string) => {
    // Just call the onSearch function, let it handle debouncing
    onSearch({ search: searchValue, limit: DEFAULT_PAGE_SIZE });
  };

  return (
    <Select
      placeholder={placeholder}
      options={enhancedOptions}
      value={value}
      onChange={handleChange}
      showSearch
      allowClear={allowClear}
      onSearch={handleSearch}
      filterOption={false}
      className={className}
      disabled={disabled}
      style={style}
      dropdownRender={(dropdown) =>
        loading ? (
          <Flex align="center" justify="center" style={{ padding: 4 }}>
            <Spin indicator={<SyncOutlined spin />} />
            <span style={{ marginLeft: 8 }}>{t('Loading')}...</span>
          </Flex>
        ) : (
          dropdown
        )
      }
    />
  );
};

export default SearchableSelect;
